import React, { useCallback, useMemo, useState } from "react";
import { Eye, Fuel, Pencil, Plus, Power, RefreshCw } from "lucide-react";
import { MessageRow, Mono, TableCard, Td, Th, Toolbar, rowClass, tableClass, tbodyClass, theadClass } from "../ui/DataPage";
import { formatINR } from "../../utils/formatINR";
import { useManagedList, useStationManagementApi } from "../../hooks/useStationManagement";
import StationFormDrawer from "./StationFormDrawer";
import { describeOperatingHours } from "./operatingHours";
import { useStatusToggle } from "./StatusToggle";
import {
  Button,
  ConfirmDialog,
  DetailGrid,
  EmptyState,
  EntityStatus,
  FilterSelect,
  IconButton,
  Overlay,
  Pagination,
  SearchInput,
  YesNo,
  formatDate,
  usePagination,
} from "./ui";

const COLS = 16;
const dash = <span className="text-neutral-400">—</span>;

export default function StationsTab({ groups, groupsLoading, onGroupSaved }) {
  const api = useStationManagementApi();
  const fetchAll = useCallback(() => api.listStations(), [api]);
  const { items: stations, isLoading, error, refetch } = useManagedList(fetchAll, [fetchAll]);

  const [search, setSearch] = useState("");
  const [groupFilter, setGroupFilter] = useState("all");
  const [locationFilter, setLocationFilter] = useState("all");
  const [status, setStatus] = useState("all");
  const [type, setType] = useState("all");
  const [editing, setEditing] = useState(undefined);
  const [viewing, setViewing] = useState(null);

  const toggle = useStatusToggle({
    entity: "station",
    describe: (s) => ({
      id: s.station_id,
      next: s.status === "active" ? "inactive" : "active",
      label: `Station ${s.station_id} · ${s.station_name}`,
    }),
    onChanged: () => refetch(),
  });

  // location filter follows the group filter
  const locationOptions = useMemo(() => {
    const seen = new Map();
    stations.forEach((s) => {
      if (groupFilter !== "all" && s.group_id !== groupFilter) return;
      if (!seen.has(s.location_id)) seen.set(s.location_id, s.location_name || s.location_id);
    });
    return [{ value: "all", label: "All locations" }, ...[...seen].map(([id, name]) => ({ value: id, label: `${id} · ${name}` }))];
  }, [stations, groupFilter]);

  const filtered = useMemo(() => {
    const q = search.trim().toLowerCase();
    return stations.filter((s) => {
      if (groupFilter !== "all" && s.group_id !== groupFilter) return false;
      if (locationFilter !== "all" && s.location_id !== locationFilter) return false;
      if (status !== "all" && s.status !== status) return false;
      if (type !== "all" && (s.station_type || "").toUpperCase() !== type) return false;
      if (!q) return true;
      return [s.station_id, s.station_name, s.display_name, s.identity, s.group_name, s.location_name, s.city, s.vendor, s.model].some((v) =>
        String(v || "").toLowerCase().includes(q)
      );
    });
  }, [stations, search, groupFilter, locationFilter, status, type]);

  const pager = usePagination(filtered, 10, `${search}|${groupFilter}|${locationFilter}|${status}|${type}`);
  const hasFilters = search || groupFilter !== "all" || locationFilter !== "all" || status !== "all" || type !== "all";
  const groupOptions = [{ value: "all", label: "All groups" }, ...groups.map((g) => ({ value: g.group_id, label: `${g.group_id} · ${g.group_name}` }))];

  return (
    <div className="space-y-4">
      <Toolbar>
        <div className="flex flex-col gap-3 xl:flex-row xl:items-center xl:justify-between">
          <div className="flex flex-1 flex-col gap-2 sm:flex-row sm:flex-wrap sm:items-center">
            <SearchInput value={search} onChange={setSearch} placeholder="Search ID, name, identity, vendor" />
            <FilterSelect
              label="Group"
              value={groupFilter}
              onChange={(v) => {
                setGroupFilter(v);
                setLocationFilter("all");
              }}
              options={groupOptions}
            />
            <FilterSelect label="Location" value={locationFilter} onChange={setLocationFilter} options={locationOptions} />
            <FilterSelect label="Status" value={status} onChange={setStatus} options={[{ value: "all", label: "All statuses" }, { value: "active", label: "Active" }, { value: "inactive", label: "Inactive" }, { value: "upcoming", label: "Upcoming" }]} />
            <FilterSelect label="Type" value={type} onChange={setType} options={[{ value: "all", label: "AC & DC" }, { value: "AC", label: "AC" }, { value: "DC", label: "DC" }]} />
          </div>
          <div className="flex gap-2">
            <Button icon={RefreshCw} onClick={refetch} disabled={isLoading}>Refresh</Button>
            <Button variant="primary" icon={Plus} onClick={() => setEditing(null)}>Add charging station</Button>
          </div>
        </div>
      </Toolbar>

      <TableCard header={<span>{filtered.length} station{filtered.length === 1 ? "" : "s"}</span>} footer={filtered.length > 0 ? <Pagination {...pager} /> : null}>
        <table className={tableClass}>
          <thead className={theadClass}>
            <tr>
              <Th>Station ID</Th>
              <Th>Station name</Th>
              <Th>Display name</Th>
              <Th>Group</Th>
              <Th>Location</Th>
              <Th align="right">Capacity</Th>
              <Th align="right">Guns</Th>
              <Th>Type</Th>
              <Th align="right">Price / kWh</Th>
              <Th>Protocol</Th>
              <Th>Vendor</Th>
              <Th>Model</Th>
              <Th>Billing</Th>
              <Th>Hours</Th>
              <Th>Status</Th>
              <Th align="right">Actions</Th>
            </tr>
          </thead>
          <tbody className={tbodyClass}>
            {isLoading && stations.length === 0 ? (
              <MessageRow colSpan={COLS}>Loading charging stations…</MessageRow>
            ) : error ? (
              <MessageRow colSpan={COLS} tone="error">{error} · <button className="underline" onClick={refetch}>Retry</button></MessageRow>
            ) : filtered.length === 0 ? (
              <tr>
                <td colSpan={COLS}>
                  <EmptyState
                    icon={Fuel}
                    title={hasFilters ? "No stations match these filters" : "No charging stations yet"}
                    description={hasFilters ? "Try a different search or clear the filters." : "Add a station and link it to a group and location."}
                    action={!hasFilters && <Button variant="primary" icon={Plus} onClick={() => setEditing(null)}>Add charging station</Button>}
                  />
                </td>
              </tr>
            ) : (
              pager.slice.map((s) => (
                <tr key={s.station_id} className={rowClass}>
                  <Td><Mono>{s.station_id}</Mono></Td>
                  <Td className="font-medium text-neutral-900">{s.station_name}</Td>
                  <Td>{s.display_name || dash}</Td>
                  <Td>{s.group_name || "—"}<span className="ml-1 text-xs text-neutral-400">#{s.group_id}</span></Td>
                  <Td>
                    <span className="block max-w-[200px] truncate" title={s.location_name}>{s.location_name || "—"}</span>
                    <span className="text-xs text-neutral-400">#{s.location_id}{s.city ? ` · ${s.city}` : ""}</span>
                  </Td>
                  <Td align="right" className="tabular-nums">{s.capacity_kw} kW</Td>
                  <Td align="right" className="tabular-nums">{s.number_of_guns}</Td>
                  <Td>{s.station_type || dash}</Td>
                  <Td align="right" className="tabular-nums">{formatPrice(s.price_per_kwh)}</Td>
                  <Td>{s.protocol_version || dash}</Td>
                  <Td>{s.vendor || dash}</Td>
                  <Td>{s.model || dash}</Td>
                  <Td><YesNo value={s.billing_enabled} yes="On" no="Off" /></Td>
                  <Td>{describeOperatingHours(s.operating_hours)}</Td>
                  <Td><EntityStatus status={s.status} /></Td>
                  <Td align="right">
                    <div className="flex justify-end gap-0.5">
                      <IconButton icon={Eye} label="View details" onClick={() => setViewing(s)} />
                      <IconButton icon={Pencil} label="Edit station" onClick={() => setEditing(s)} />
                      <IconButton icon={Power} label={s.status === "active" ? "Deactivate" : "Activate"} onClick={() => toggle.request(s)} className={s.status === "active" ? "hover:!text-red-600" : "hover:!text-green-700"} />
                    </div>
                  </Td>
                </tr>
              ))
            )}
          </tbody>
        </table>
      </TableCard>

      <StationFormDrawer
        open={editing !== undefined}
        station={editing}
        groups={groups}
        groupsLoading={groupsLoading}
        onGroupsChanged={onGroupSaved}
        onClose={() => setEditing(undefined)}
        onSaved={() => refetch()}
      />

      <Overlay
        open={Boolean(viewing)}
        onClose={() => setViewing(null)}
        variant="drawer"
        width="max-w-lg"
        title={viewing ? `Station ${viewing.station_id}` : ""}
        subtitle={viewing?.station_name}
        footer={
          viewing && (
            <Button
              variant="primary"
              icon={Pencil}
              onClick={() => {
                setEditing(viewing);
                setViewing(null);
              }}
            >
              Edit station
            </Button>
          )
        }
      >
        {viewing && (
          <div className="space-y-6">
            <DetailBlock title="Basic">
              <DetailGrid
                items={[
                  ["Station ID", viewing.station_id],
                  ["Status", <EntityStatus status={viewing.status} />],
                  ["Station name", viewing.station_name],
                  ["Display name", viewing.display_name],
                  ["Type", viewing.station_type],
                  ["Capacity", `${viewing.capacity_kw} kW`],
                  ["Number of guns", viewing.number_of_guns],
                ]}
              />
            </DetailBlock>
            <DetailBlock title="Group & location">
              <DetailGrid
                items={[
                  ["Group", `${viewing.group_id} · ${viewing.group_name}`, true],
                  ["Location", `${viewing.location_id} · ${viewing.location_name || ""}${viewing.city ? `, ${viewing.city}` : ""}`, true],
                ]}
              />
            </DetailBlock>
            <DetailBlock title="Charging">
              <DetailGrid
                items={[
                  ["Price per kWh", formatPrice(viewing.price_per_kwh)],
                  ["Billing", <YesNo value={viewing.billing_enabled} yes="On" no="Off" />],
                  ["Operating hours", describeOperatingHours(viewing.operating_hours), true],
                ]}
              />
            </DetailBlock>
            <DetailBlock title="Technical">
              <DetailGrid
                items={[
                  ["Station identity", <span className="font-mono text-[12.5px]">{viewing.identity}</span>],
                  ["Protocol", viewing.protocol_version],
                  ["Vendor", viewing.vendor],
                  ["Model", viewing.model],
                  ["Phone", viewing.phone],
                  ["Email", viewing.email],
                ]}
              />
            </DetailBlock>
            <DetailBlock title="Installation">
              <DetailGrid
                items={[
                  ["Installed", formatDate(viewing.installation_date)],
                  ["Last maintenance", formatDate(viewing.last_maintenance_date)],
                ]}
              />
            </DetailBlock>
          </div>
        )}
      </Overlay>

      <ConfirmDialog {...toggle.dialogProps} />
    </div>
  );
}

const DetailBlock = ({ title, children }) => (
  <section>
    <h3 className="mb-3 border-b border-neutral-100 pb-2 text-xs font-semibold uppercase tracking-[0.06em] text-neutral-900">{title}</h3>
    {children}
  </section>
);

const formatPrice = (v) => formatINR(v);
